import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Sparkles, Loader2, Send, Mail, AlertCircle, RefreshCw, X as XIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/**
 * Bulk Post-Meeting Email composer. Lists every eligible (linked +
 * email) row inside a conference folder, lets the user tweak assets +
 * notes per row, generates Claude-personalised previews in one batch,
 * then fires the whole batch through Gmail with 8-sec throttling.
 */
export default function BulkPostMeetingEmail({ conferenceName, rows, open, onClose, onSent }) {
  const eligible = (rows || []).filter(r => r.match_status === 'linked' && r.email && r.biotech_company_id);
  const [items, setItems] = useState([]);  // {row, asset_ids, notes, preview, included, error}
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null); // {task_id, sent, failed, total}
  const [biotechAssets, setBiotechAssets] = useState({}); // biotech_id → [assets]

  useEffect(() => {
    if (!open) return;
    setItems(eligible.map(r => ({
      row: r,
      asset_ids: r.assets_discussed || [],
      notes: r.notes || '',
      preview: null,
      included: true,
      error: null,
    })));
    setProgress(null);
    // Bulk-fetch assets per biotech (dedup)
    const uniqueBiotechs = [...new Set(eligible.map(r => r.biotech_company_id))];
    Promise.all(uniqueBiotechs.map(bid =>
      axios.get(`${API}/assets?company_id=${bid}`, { withCredentials: true })
        .then(r => [bid, r.data?.items || r.data || []])
        .catch(() => [bid, []])
    )).then(pairs => {
      const map = {};
      pairs.forEach(([bid, assets]) => { map[bid] = assets; });
      setBiotechAssets(map);
    });
  }, [open]); // eslint-disable-line

  const update = (idx, patch) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const generateAll = async () => {
    setGenerating(true);
    try {
      const payload = {
        items: items.filter(it => it.included).map(it => ({
          row_id: it.row.id,
          asset_ids: it.asset_ids,
          custom_notes: it.notes,
        })),
      };
      const r = await axios.post(`${API}/met/post-meeting-email/preview-bulk`, payload, { withCredentials: true });
      const previews = r.data?.previews || [];
      const byId = Object.fromEntries(previews.map(p => [p.row_id, p]));
      setItems(prev => prev.map(it => {
        const p = byId[it.row.id];
        if (!p) return it;
        if (p.ok === false) return { ...it, preview: null, error: p.error };
        return { ...it, preview: p, error: null };
      }));
      toast.success(`Generated ${previews.filter(p => p.ok !== false).length} previews`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview generation failed');
    } finally { setGenerating(false); }
  };

  const sendAll = async (testMode = false) => {
    const toSend = items.filter(it => it.included && it.preview);
    if (toSend.length === 0) { toast.error('No previewed emails to send. Generate previews first.'); return; }
    const testEmail = testMode
      ? (window.prompt(`Send all ${toSend.length} TEST emails to which address?`, 'amit.dang@foresightrx.com') || '').trim()
      : '';
    if (testMode && !testEmail) return;
    if (!testMode && !window.confirm(`Send ${toSend.length} post-meeting emails from amit.dang@foresightrx.com?\n\nA new Outreach row will be created per recipient. Sends are throttled 8 seconds apart (~${Math.ceil(toSend.length * 8 / 60)} min total).`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/met/post-meeting-email/send`, {
        items: toSend.map(it => ({ row_id: it.row.id, asset_ids: it.asset_ids, custom_notes: it.notes })),
        ...(testEmail ? { test_email_override: testEmail } : {}),
      }, { withCredentials: true });
      setProgress({ task_id: r.data.task_id, total: r.data.queued, sent: 0, failed: 0 });
      toast.success(testMode
        ? `Queued ${r.data.queued} TEST sends → ${testEmail}`
        : `Queued ${r.data.queued} sends — polling progress`);
      pollProgress(r.data.task_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
      setSending(false);
    }
  };

  const pollProgress = async (taskId) => {
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/met/post-meeting-email/status/${taskId}`, { withCredentials: true });
        const j = r.data;
        setProgress({ task_id: taskId, total: j.total, sent: j.sent, failed: j.failed, errors: j.errors || [] });
        if (j.finished_at) {
          setSending(false);
          toast.success(`Done — ${j.sent} sent · ${j.failed} failed`);
          onSent?.();
          return;
        }
        setTimeout(tick, 4000);
      } catch {
        setSending(false);
      }
    };
    tick();
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !sending && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="bulk-pme-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            Bulk Post-Meeting Emails — {conferenceName}
          </DialogTitle>
          <DialogDescription>
            {eligible.length} eligible (linked + has email) of {rows?.length || 0} meeting rows.
            Sender: amit.dang@foresightrx.com · throttled 8s between sends to avoid spam-detection.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 sticky top-0 bg-white py-2 z-10 border-b">
          <Button onClick={generateAll} disabled={generating || sending} variant="outline" size="sm" data-testid="bulk-pme-generate-btn">
            {generating ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating {items.filter(i=>i.included).length}…</> : <><Sparkles className="w-3 h-3 mr-1" /> Generate all previews</>}
          </Button>
          <Button onClick={() => sendAll(true)} disabled={sending || items.filter(it => it.included && it.preview).length === 0} variant="outline" size="sm" data-testid="bulk-pme-test-btn" className="border-amber-300 text-amber-800 hover:bg-amber-50">
            <Mail className="w-3 h-3 mr-1" /> Send tests to me
          </Button>
          <Button onClick={() => sendAll(false)} disabled={sending || items.filter(it => it.included && it.preview).length === 0} className="bg-emerald-600 hover:bg-emerald-700" size="sm" data-testid="bulk-pme-send-btn">
            {sending ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…</> : <><Send className="w-3 h-3 mr-1" /> Send all ({items.filter(it => it.included && it.preview).length})</>}
          </Button>
          {progress && (
            <div className="text-xs text-slate-500 ml-auto">
              {progress.sent}/{progress.total} sent · {progress.failed} failed
            </div>
          )}
        </div>

        <div className="space-y-3 mt-3">
          {items.length === 0 && (
            <div className="text-center py-10 text-slate-400 text-sm">No eligible rows in this conference yet.</div>
          )}
          {items.map((it, idx) => {
            const assets = biotechAssets[it.row.biotech_company_id] || [];
            return (
              <div key={it.row.id} className={`border rounded-lg p-3 ${!it.included ? 'opacity-50' : ''} ${it.error ? 'border-rose-200 bg-rose-50/30' : 'border-slate-200'}`}>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={it.included}
                    onChange={(e) => update(idx, { included: e.target.checked })}
                    className="mt-1"
                    disabled={sending}
                    data-testid={`bulk-pme-include-${it.row.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">
                      {it.row.name || '(no name)'} <span className="text-slate-400 font-normal">· {it.row.company_name}</span>
                    </div>
                    <div className="text-[11px] text-slate-500"><Mail className="w-3 h-3 inline mr-0.5" /> {it.row.email}</div>

                    {/* Asset pills */}
                    {assets.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {assets.map(a => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => {
                              const next = it.asset_ids.includes(a.id)
                                ? it.asset_ids.filter(x => x !== a.id)
                                : [...it.asset_ids, a.id];
                              update(idx, { asset_ids: next, preview: null });
                              axios.patch(`${API}/met/${it.row.id}/assets-discussed`, { asset_ids: next }, { withCredentials: true }).catch(()=>{});
                            }}
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${it.asset_ids.includes(a.id) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`}
                            disabled={sending}
                          >
                            {a.asset_name || a.name}
                            {a.stage && <span className="ml-1 opacity-70">{a.stage}</span>}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Notes */}
                    <Textarea
                      value={it.notes}
                      onChange={(e) => update(idx, { notes: e.target.value, preview: null })}
                      rows={2}
                      placeholder="Meeting notes — sent verbatim, no word limit, also feeds Claude personalization context"
                      className="mt-2 text-xs"
                      disabled={sending}
                    />

                    {/* Preview */}
                    {it.preview && (
                      <div className="mt-2 border border-emerald-200 bg-emerald-50/30 rounded p-2 text-xs">
                        <div className="text-[10px] text-slate-500 mb-1">Subject: <span className="text-slate-800 font-medium">{it.preview.subject}</span></div>
                        {it.preview.ai_paragraph && (
                          <div className="text-emerald-800 italic">
                            <Sparkles className="w-3 h-3 inline mr-0.5" /> {it.preview.ai_paragraph}
                          </div>
                        )}
                      </div>
                    )}
                    {it.error && (
                      <div className="mt-2 text-xs text-rose-700"><AlertCircle className="w-3 h-3 inline mr-0.5" /> {it.error}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {progress && progress.errors && progress.errors.length > 0 && (
          <div className="mt-3 border border-rose-200 bg-rose-50 rounded p-2 text-xs">
            <div className="font-semibold text-rose-700 mb-1">{progress.errors.length} failures</div>
            {progress.errors.slice(0, 5).map((e, i) => (
              <div key={i} className="text-rose-600">{e.row_id?.slice(0, 8)}: {e.error}</div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
